/**
 * MED-781: Compact Active Cohorts list (AC18–AC22).
 *
 * - Shows cohort name, client (partner), date range, and status badge.
 * - Filter (AC20, Q3): status-based — exclude only `completed` and `archived`.
 *   A Live cohort whose end date has slipped but hasn't formally closed stays.
 * - Sort (AC21, Q2): Live → In Prep → In Design first; secondary sort by
 *   start date soonest-first within each status group.
 * - Row click navigates to the cohort detail (AC22, unchanged).
 */

import { Card, Group, Stack, Text, Title, Badge, Skeleton, Box, UnstyledButton, ThemeIcon } from '@mantine/core';
import { Users, ChevronRight } from 'react-feather';
import { EmptyState } from '../../../../components/common';
import { COHORT_STATUS_CONFIG } from '../../../../config/statusConfig';
import type { CoachCohort } from '../../../../services/api/coachPortal';
import type { CohortStatus } from '../../../../types';
import { parseDateOnly } from '../../../../utils/dateHelpers';

interface ActiveCohortsCompactListProps {
  cohorts: CoachCohort[];
  isLoading: boolean;
  onCohortClick: (cohortId: string) => void;
}

const EXCLUDED_STATUSES = new Set<CohortStatus>(['completed', 'archived']);

const STATUS_SORT_INDEX: Partial<Record<CohortStatus, number>> = {
  live: 0,
  in_prep: 1,
  in_design: 2,
  // Anything else (draft, wrap_up, cancelled if not filtered) sorts after — it
  // shouldn't appear often, but if it does it stays visible to avoid hiding
  // unexpected state.
};

function getStatusSortIndex(status: string): number {
  return STATUS_SORT_INDEX[status as CohortStatus] ?? 99;
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start) return 'TBD';
  // MED-1265: DATE columns arrive as UTC-midnight timestamps; parse as calendar dates
  const startDate = parseDateOnly(start)!;
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  if (!end) return startDate.toLocaleDateString('en-US', opts);
  const endDate = parseDateOnly(end)!;
  return `${startDate.toLocaleDateString('en-US', opts)} - ${endDate.toLocaleDateString('en-US', opts)}`;
}

export function ActiveCohortsCompactList({
  cohorts,
  isLoading,
  onCohortClick,
}: ActiveCohortsCompactListProps) {
  const visible = cohorts
    .filter(c => !EXCLUDED_STATUSES.has(c.status as CohortStatus))
    .sort((a, b) => {
      const sa = getStatusSortIndex(a.status);
      const sb = getStatusSortIndex(b.status);
      if (sa !== sb) return sa - sb;
      const da = a.start_date ? new Date(a.start_date).getTime() : Number.POSITIVE_INFINITY;
      const db = b.start_date ? new Date(b.start_date).getTime() : Number.POSITIVE_INFINITY;
      return da - db;
    });

  return (
    <Card withBorder padding="lg" radius="md">
      <Group justify="space-between" mb="md">
        <Group gap="xs">
          <ThemeIcon size="sm" variant="light" color="gray" radius="sm">
            <Users size={14} />
          </ThemeIcon>
          <Title order={4}>Active Cohorts</Title>
        </Group>
      </Group>

      {isLoading ? (
        <>
          {/* MED-1394: row-sized placeholders, so the page does not jump on load. */}
          <Stack gap="xs">
            {Array.from({ length: 3 }, (_, i) => <Skeleton key={i} height={56} radius="sm" />)}
          </Stack>
        </>
      ) : visible.length === 0 ? (
        <EmptyState
          size="sm"
          icon={<Users size={20} />}
          title="No active cohorts"
          description="Live, In Prep, and In Design cohorts will appear here."
        />
      ) : (
        <Stack gap="xs">
          {visible.map(cohort => (
            <CohortRow key={cohort.id} cohort={cohort} onClick={() => onCohortClick(cohort.id)} />
          ))}
        </Stack>
      )}
    </Card>
  );
}

interface CohortRowProps {
  cohort: CoachCohort;
  onClick: () => void;
}

function CohortRow({ cohort, onClick }: CohortRowProps) {
  const config = COHORT_STATUS_CONFIG[cohort.status as CohortStatus];
  return (
    <UnstyledButton
      onClick={onClick}
      w="100%"
      style={{ borderRadius: 6 }}
    >
      <Group
        justify="space-between"
        wrap="nowrap"
        py="xs"
        px="sm"
        style={{ borderRadius: 6 }}
      >
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} lineClamp={1}>
            {cohort.name}
          </Text>
          <Text size="xs" c="dimmed" lineClamp={1}>
            {cohort.partner_name || 'No Partner'} · {formatDateRange(cohort.start_date, cohort.end_date)}
          </Text>
        </Box>
        <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0 }}>
          <Badge
            size="sm"
            color={config?.color || 'gray'}
            variant={config?.variant ?? 'light'}
          >
            {config?.label ?? cohort.status}
          </Badge>
          <ChevronRight size={16} color="var(--mantine-color-dimmed)" />
        </Group>
      </Group>
    </UnstyledButton>
  );
}
